import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  FileText, 
  FilePlus,
  Clock,
  MoreVertical,
  Pencil,
  Trash2,
  FileType,
  ClipboardList,
  BookOpen,
  ScrollText,
  Users
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPE_INFO = {
  memo: { icon: FileText, label: "Memo", color: "bg-blue-100 text-blue-700" },
  training: { icon: BookOpen, label: "Training", color: "bg-purple-100 text-purple-700" },
  quick_reference: { icon: FileType, label: "Quick Ref", color: "bg-teal-100 text-teal-700" },
  checklist: { icon: ClipboardList, label: "Checklist", color: "bg-green-100 text-green-700" },
  policy: { icon: ScrollText, label: "Policy", color: "bg-orange-100 text-orange-700" },
  patient_education: { icon: Users, label: "Patient Ed", color: "bg-pink-100 text-pink-700" }
};

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}`);
      if (!res.ok) throw new Error("Project not found");
      const data = await res.json();
      setProject(data.project);
      setDocuments(data.documents || []);
    } catch (error) {
      toast.error("Failed to load project");
      navigate("/projects");
    }
    setLoading(false);
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    
    try {
      const res = await fetch(`${API_URL}/api/documents/${docId}`, {
        method: "DELETE"
      });
      if (res.ok) {
        toast.success("Document deleted");
        setDocuments(documents.filter(d => d.id !== docId));
      }
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/projects")}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div 
          className="w-12 h-12 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: project?.color || "#6A26B5" }}
        >
          <FileText className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">{project?.name}</h1>
          <p className="text-sm text-slate-500">{project?.description || "No description"}</p>
        </div>
        <Button 
          onClick={() => navigate(`/documents/new?project=${projectId}`)}
          className="bg-[#6A26B5] hover:bg-[#5a1f9e]"
          data-testid="new-document-btn"
        >
          <FilePlus className="w-4 h-4 mr-2" />
          New Document
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-[#6A26B5]">{documents.length}</div>
            <p className="text-sm text-slate-500">Total Documents</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-green-600">
              {documents.filter(d => d.status === "published").length}
            </div>
            <p className="text-sm text-slate-500">Published</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-amber-600">
              {documents.filter(d => d.status === "draft").length}
            </div>
            <p className="text-sm text-slate-500">Drafts</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <Badge className={
              project?.status === "active" ? "bg-green-100 text-green-700" :
              project?.status === "completed" ? "bg-blue-100 text-blue-700" :
              "bg-slate-100 text-slate-600"
            }>
              {project?.status}
            </Badge>
            <p className="text-sm text-slate-500 mt-1">Project Status</p>
          </CardContent>
        </Card>
      </div>

      {/* Documents List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Documents</CardTitle>
          <CardDescription>All documents in this project</CardDescription>
        </CardHeader>
        <CardContent>
          {documents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">No documents yet</h3>
              <p className="text-sm text-slate-500 mb-4">
                Create your first document for this project
              </p>
              <Button 
                onClick={() => navigate(`/documents/new?project=${projectId}`)}
                className="bg-[#6A26B5] hover:bg-[#5a1f9e]"
              >
                <FilePlus className="w-4 h-4 mr-2" />
                Create Document
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {documents.map((doc) => {
                const typeInfo = DOC_TYPE_INFO[doc.doc_type] || DOC_TYPE_INFO.memo;
                const TypeIcon = typeInfo.icon;
                
                return (
                  <div 
                    key={doc.id} 
                    className="flex items-center justify-between py-4 group hover:bg-slate-50 -mx-4 px-4 cursor-pointer"
                    onClick={() => navigate(`/documents/${doc.id}`)}
                    data-testid={`document-row-${doc.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${typeInfo.color}`}>
                        <TypeIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900 group-hover:text-[#6A26B5]">
                          {doc.title}
                        </h4>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Badge variant="outline" className="text-xs">{typeInfo.label}</Badge>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(doc.updated_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={doc.status === "published" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                        {doc.status}
                      </Badge>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/documents/${doc.id}`);
                          }}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteDocument(doc.id);
                            }}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
